<script setup lang="ts">
import { ref, computed } from "vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import DMSuiteIcon from "@/components/iconify/DMSuiteIcon.vue";
import { DASHBOARD_ICONS } from "@/config/dashboardIcons";
import ReportDownloadPreviewModal from "./ReportDownloadPreviewModal.vue";

const props = defineProps<{
  reports: any[];
  loading?: boolean;
}>();

const emit = defineEmits<{
  (event: "download", report: any): void;
  (event: "preview", report: any): void;
}>();

const selectedReport = ref<any>(null);
const showPreviewModal = ref(false);

const publishedReports = computed(() => {
  if (!Array.isArray(props.reports)) return [];
  return props.reports;
});

const latestReport = computed(() => {
  return publishedReports.value.length > 0 ? publishedReports.value[0] : null;
});

const olderReports = computed(() => {
  return publishedReports.value.slice(1, 4);
});

function handleDownloadClick(report: any) {
  selectedReport.value = report;
  showPreviewModal.value = true;
}

function handleConfirmDownload() {
  if (selectedReport.value) {
    emit("download", selectedReport.value);
  }
  showPreviewModal.value = false;
}

function formatDate(value?: string | null) {
  if (!value) return "Published recently";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Published recently";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
</script>

<template>
  <div
    class="text-left flex min-w-0 w-full flex-col overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm p-6 md:p-8 space-y-6"
  >
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100/70 pb-4">
      <div>
        <h3 class="font-black text-slate-900 text-xl md:text-2xl">
          Latest Progress Report
        </h3>
        <p class="font-semibold text-sm md:text-base text-slate-600 mt-1">
          Review your marketing performance analytics and campaign highlights.
        </p>
      </div>
      <Badge
        v-if="latestReport"
        variant="outline"
        class="bg-emerald-50 text-emerald-700 border-emerald-200/80 font-bold px-3 py-1 rounded-full text-xs self-start sm:self-center"
      >
        Latest Available Report
      </Badge>
    </div>

    <!-- Loading State -->
    <div
      v-if="loading"
      class="space-y-4"
    >
      <Skeleton class="h-36 rounded-2xl bg-slate-100 animate-pulse" />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="!latestReport"
      class="border border-dashed border-slate-200 bg-slate-50/40 p-10 rounded-2xl text-center space-y-3"
    >
      <div class="size-12 rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center mx-auto">
        <DMSuiteIcon
          :icon="DASHBOARD_ICONS.eventReport"
          :size="24"
          custom-class="text-slate-400"
        />
      </div>
      <div class="space-y-1">
        <p class="text-base font-bold text-slate-800">
          No reports published yet
        </p>
        <p class="text-xs text-slate-400 font-medium max-w-sm mx-auto">
          Your published monthly progress reports will appear here as soon as they are compiled and finalized by your team.
        </p>
      </div>
    </div>

    <!-- Featured Latest Report -->
    <div
      v-else
      class="space-y-6"
    >
      <div class="rounded-2xl border border-blue-100/80 bg-gradient-to-r from-blue-50/40 via-white to-white p-6 md:p-7 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div class="space-y-3 flex-1 min-w-0">
          <div class="flex flex-wrap items-center gap-2.5">
            <span class="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-blue-100/70 text-[#1f7aff] text-xs font-black">
              <DMSuiteIcon
                :icon="DASHBOARD_ICONS.calendar"
                :size="13"
              />
              {{ latestReport.report_period || 'Monthly Summary' }}
            </span>
            <span class="text-xs font-semibold text-slate-400">
              Published on {{ formatDate(latestReport.created_at) }}
            </span>
          </div>

          <div>
            <h4 class="text-lg md:text-xl font-black text-slate-900 tracking-tight">
              {{ latestReport.title || 'Digital Marketing Performance Report' }}
            </h4>
            <p class="text-xs md:text-sm text-slate-600 font-medium mt-1 leading-relaxed">
              {{ latestReport.summary || latestReport.description || 'Comprehensive evaluation of campaign reach, active service deliverables, audience interactions, and progress milestones.' }}
            </p>
          </div>
        </div>

        <!-- Working Actions -->
        <div class="flex flex-wrap items-center gap-2.5 shrink-0">
          <Button
            size="sm"
            variant="outline"
            class="border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-bold rounded-xl h-10 px-4 text-xs md:text-sm shadow-xs flex items-center gap-2 transition-all"
            @click="$emit('preview', latestReport)"
          >
            <DMSuiteIcon
              :icon="DASHBOARD_ICONS.preview"
              :size="16"
              custom-class="text-[#1f7aff]"
            />
            View Report
          </Button>

          <Button
            size="sm"
            class="bg-[#1f7aff] hover:bg-[#185adb] text-white font-bold rounded-xl h-10 px-4 text-xs md:text-sm shadow-xs flex items-center gap-2 transition-all"
            @click="handleDownloadClick(latestReport)"
          >
            <DMSuiteIcon
              :icon="DASHBOARD_ICONS.download"
              :size="16"
            />
            Download PDF
          </Button>
        </div>
      </div>

      <!-- Prior Reports Archive (if multiple reports exist) -->
      <div
        v-if="olderReports.length > 0"
        class="border-t border-slate-100 pt-4 space-y-3"
      >
        <h5 class="text-xs font-extrabold text-slate-400 uppercase tracking-wider">
          Previous Reports
        </h5>
        <div class="divide-y divide-slate-100">
          <div
            v-for="report in olderReports"
            :key="report.id"
            class="py-3 flex items-center justify-between gap-4"
          >
            <div class="min-w-0 flex items-center gap-3">
              <div class="size-9 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                <DMSuiteIcon
                  :icon="DASHBOARD_ICONS.eventReport"
                  :size="16"
                  custom-class="text-slate-500"
                />
              </div>
              <div class="min-w-0">
                <p class="text-sm font-bold text-slate-800 truncate">
                  {{ report.title }}
                </p>
                <p class="text-xs text-slate-400 font-medium">
                  {{ report.report_period }} • {{ formatDate(report.created_at) }}
                </p>
              </div>
            </div>

            <div class="flex items-center gap-2 shrink-0">
              <Button
                size="sm"
                variant="ghost"
                class="text-xs font-bold text-[#1f7aff] hover:bg-blue-50/50 h-8 px-2.5 rounded-lg"
                @click="$emit('preview', report)"
              >
                View
              </Button>
              <Button
                size="sm"
                variant="ghost"
                class="text-xs font-bold text-slate-600 hover:bg-slate-100 h-8 px-2.5 rounded-lg"
                @click="handleDownloadClick(report)"
              >
                PDF
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Download Preview Modal -->
    <ReportDownloadPreviewModal
      v-model:open="showPreviewModal"
      :report="selectedReport"
      @confirm-download="handleConfirmDownload"
    />
  </div>
</template>
